import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type BioBackgroundStyle = "solid" | "gradient" | "image";
export type BioButtonStyle = "filled" | "outline" | "soft";
export type BioButtonRadius = "none" | "md" | "lg" | "full";
export type BioSocialStyle = "abbr" | "icon";

export interface BioProfile {
  id: 1;
  display_name: string;
  tagline: string | null;
  avatar_url: string | null;
  background_style: BioBackgroundStyle;
  background_color: string;
  background_color_end: string | null;
  background_image_url: string | null;
  background_overlay_opacity: number;
  button_style: BioButtonStyle;
  button_color: string;
  button_text_color: string;
  button_radius: BioButtonRadius;
  text_color: string;
  muted_text_color: string;
  subtle_text_color: string;
  card_color: string;
  card_border_color: string;
  social_style: BioSocialStyle;
  footer_text: string;
  show_footer: boolean;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

export type UpdateBioProfileData = Partial<Omit<BioProfile, "id" | "created_at" | "updated_at">>;

// Lê o perfil/tema. Como admin sempre vê a linha (policy própria), este mesmo
// hook serve tanto o admin quanto a página pública (que só vê a linha se
// is_published=true, via RLS).
export function useBioProfile() {
  return useQuery({
    queryKey: ["bio-profile"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bio_profile" as never)
        .select("*")
        .eq("id", 1)
        .maybeSingle();

      if (error) throw error;
      return data as unknown as BioProfile | null;
    },
  });
}

export function useUpdateBioProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateBioProfileData) => {
      const { data: profile, error } = await supabase
        .from("bio_profile" as never)
        .update(data as never)
        .eq("id", 1)
        .select()
        .single();

      if (error) throw error;
      return profile as unknown as BioProfile;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bio-profile"] });
      toast.success("Aparência atualizada com sucesso!");
    },
    onError: (error) => {
      toast.error("Erro ao atualizar aparência: " + error.message);
    },
  });
}
